import Link from 'next/link';
import Image from 'next/image';
import {
  MapPin,
  Phone,
  Mail,
  Linkedin,
  Facebook,
  Twitter,
  ArrowRight,
  MessageCircle
} from 'lucide-react';
import { getTranslations, getLocale } from 'next-intl/server';
import { getSiteConfig } from '@/lib/config';
import NewsletterForm from '@/components/ui/NewsletterForm';

export default async function Footer() {
  const config = await getSiteConfig();
  const locale = await getLocale();
  const t = await getTranslations('footer');
  const tNav = await getTranslations('nav');
  const tExpertise = await getTranslations('expertiseMenu');

  const footerLinks = {
    expertises: [
      { name: tExpertise('studiesResearch'), href: `/${locale}/expertises/etudes-recherche` },
      { name: tExpertise('monitoringEvaluation'), href: `/${locale}/expertises/suivi-evaluation` },
      { name: tExpertise('csrAdvice'), href: `/${locale}/expertises/rse` },
      { name: tExpertise('projectManagement'), href: `/${locale}/expertises/gestion-projets` },
      { name: tExpertise('dataAnalysis'), href: `/${locale}/expertises/veille-analyse` },
    ],
    quickLinks: [
      { name: tNav('about'), href: `/${locale}/a-propos` },
      { name: tNav('formations'), href: `/${locale}/formations` },
      { name: tNav('references'), href: `/${locale}/references` },
      { name: tNav('contact'), href: `/${locale}/contact` },
    ],
  };

  return (
    <footer className="bg-cerad-green text-white">
      {/* Newsletter Section */}
      <div className="border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-5">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-8">
            <div>
              <h3 className="font-montserrat text-2xl font-bold mb-2">
                {t('newsletter.title')}
              </h3>
              <p className="text-white/70 text-sm">
                {t('newsletter.description')}
              </p>
            </div>
            <NewsletterForm
              placeholder={t('newsletter.placeholder')}
              buttonText={t('newsletter.subscribe')}
              source="footer"
              variant="footer"
            />
          </div>
        </div>
      </div>

      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-5">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-16">
          {/* About */}
          <div className="sm:col-span-2 lg:col-span-1">
            <div className="flex items-center gap-4 mb-8">
              <div className="relative w-12 h-12 flex-shrink-0">
                <Image
                  src="/logo.png"
                  alt={`${config.companyName} Logo`}
                  fill
                  sizes="48px"
                  className="object-contain"
                />
              </div>
              <div>
                <span className="font-montserrat font-bold text-xl">{config.companyName}</span>
                <p className="text-xs text-white/60">{config.companyFullName}</p>
              </div>
            </div>
            <p className="text-white/70 mb-8 leading-relaxed">
              {config.slogan}. {config.description}
            </p>
            <div className="flex gap-4">
              {config.linkedin && (
                <a
                  href={config.linkedin}
                  target="_blank"
                  rel="noopener noreferrer"
                  title="LinkedIn"
                  aria-label="LinkedIn"
                  className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-cerad-gold transition-colors"
                >
                  <Linkedin className="w-5 h-5" />
                </a>
              )}
              {config.facebook && (
                <a
                  href={config.facebook}
                  target="_blank"
                  rel="noopener noreferrer"
                  title="Facebook"
                  aria-label="Facebook"
                  className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-cerad-gold transition-colors"
                >
                  <Facebook className="w-5 h-5" />
                </a>
              )}
              {config.twitter && (
                <a
                  href={config.twitter}
                  target="_blank"
                  rel="noopener noreferrer"
                  title="Twitter"
                  aria-label="Twitter"
                  className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-cerad-gold transition-colors"
                >
                  <Twitter className="w-5 h-5" />
                </a>
              )}
              {config.whatsapp && (
                <a
                  href={`https://wa.me/${config.whatsapp.replace(/[^0-9]/g, '')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  title="WhatsApp"
                  aria-label="WhatsApp"
                  className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-cerad-gold transition-colors"
                >
                  <MessageCircle className="w-5 h-5" />
                </a>
              )}
            </div>
          </div>

          {/* Expertises */}
          <div>
            <h4 className="font-montserrat font-bold text-lg mb-8">{t('expertises')}</h4>
            <ul className="space-y-4">
              {footerLinks.expertises.map((link) => (
                <li key={link.name}>
                  <Link
                    href={link.href}
                    className="text-white/70 hover:text-cerad-gold transition-colors flex items-center gap-2"
                  >
                    <ArrowRight className="w-4 h-4" />
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-montserrat font-bold text-lg mb-8">{t('quickLinks')}</h4>
            <ul className="space-y-4">
              {footerLinks.quickLinks.map((link) => (
                <li key={link.name}>
                  <Link
                    href={link.href}
                    className="text-white/70 hover:text-cerad-gold transition-colors flex items-center gap-2"
                  >
                    <ArrowRight className="w-4 h-4" />
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-montserrat font-bold text-lg mb-8">{t('contact')}</h4>
            <ul className="space-y-5">
              <li className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-cerad-gold flex-shrink-0 mt-1" />
                <span className="text-white/70">
                  {config.city}, {config.country}<br />
                  {config.address}
                </span>
              </li>
              <li className="flex items-center gap-3">
                <Phone className="w-5 h-5 text-cerad-gold flex-shrink-0" />
                <a href={`tel:${config.phone?.replace(/\s/g, '') || ''}`} className="text-white/70 hover:text-cerad-gold transition-colors">
                  {config.phone}
                </a>
              </li>
              <li className="flex items-center gap-3">
                <Mail className="w-5 h-5 text-cerad-gold flex-shrink-0" />
                <a href={`mailto:${config.email}`} className="text-white/70 hover:text-cerad-gold transition-colors">
                  {config.email}
                </a>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-4 mt-2">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4 text-white/60 text-sm">
            <p>© {new Date().getFullYear()} {config.companyName}. {t('rights')}</p>
            <div className="flex gap-6">
              <Link href={`/${locale}/mentions-legales`} className="hover:text-cerad-gold transition-colors">
                {t('legal')}
              </Link>
              <Link href={`/${locale}/politique-confidentialite`} className="hover:text-cerad-gold transition-colors">
                {t('privacy')}
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
